import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import {
  AppBar,
  CardActions,
  CardContent,
  Dialog,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Toolbar,
  Typography,
} from "@mui/material";
import PrimaryButton from "components/PrimaryButton";
import StyledCard from "components/StyledCard";
import { StyledContainer } from "components/StyledContainer";
import { PartType } from "operations/schema/schema";
import { FC, useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

interface ScannerPartSuggestDialogProps {
  open: boolean;
  scannerValue: string | null;
  parts: PartType[];
  selectPart: (selectedPart: PartType) => void;
  onClose: () => void;
}

export const ScannerPartSuggestDialog: FC<ScannerPartSuggestDialogProps> = (props) => {
  const { open, scannerValue, parts, selectPart, onClose } = props;
  const intl = useIntl();
  const [selectedPart, setSelectedPart] = useState<PartType | null>(null);

  const handleClose = useCallback(() => {
    setSelectedPart(null);
    onClose();
  }, [onClose]);

  const onSelect = useCallback(() => {
    if (selectedPart) selectPart(selectedPart);
    handleClose();
  }, [handleClose, selectPart, selectedPart]);

  return (
    <Dialog fullScreen open={open} onClose={handleClose} data-testid="ScannerPartSuggestDialog">
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label={intl.formatMessage({ id: "general.back" })}
            size="large"
            data-testid="ScannerPartSuggestDialog-CloseButton"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" marginLeft="16px" flex={1}>
            <FormattedMessage id="barcode.suggestedMatches" />
          </Typography>
        </Toolbar>
      </AppBar>
      <Toolbar />
      <StyledContainer sx={{ mt: 1 }}>
        <Grid container alignItems="center" minWidth="100%" mt={2}>
          {intl.formatMessage({ id: "barcode.noMatchSuggested" }, { scannedValue: scannerValue })}
        </Grid>
        <Grid container alignItems="center" minWidth="100%" mt={2}>
          <RadioGroup row value={selectedPart} name="selectedPart" sx={{ minWidth: "100%" }}>
            {parts.map((part) => (
              <Grid item alignItems="center" key={part.articleNo} xs={12}>
                <StyledCard onClick={() => setSelectedPart(part)}>
                  <CardContent sx={{ width: "100%" }}>
                    <Typography variant="body1" component="h3">
                      {part.description}
                    </Typography>
                    <Typography color="textSecondary">{part.articleNo}</Typography>
                    <Typography color="textSecondary">{part.barcode}</Typography>
                    <Typography variant="body2" component="p">
                      <FormattedMessage id="general.quantity" />: {part.quantity}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Radio value={part} onClick={() => setSelectedPart(part)} />
                  </CardActions>
                </StyledCard>
              </Grid>
            ))}
          </RadioGroup>
        </Grid>
        <Grid item>
          <PrimaryButton
            fullWidth
            variant="contained"
            onClick={onSelect}
            disabled={!selectedPart}
            data-testid="ScannerPartSuggestDialog-AddPart"
          >
            {intl.formatMessage({
              id: "barcode.select",
            })}
          </PrimaryButton>
        </Grid>
      </StyledContainer>
    </Dialog>
  );
};
